// React bindings for the replica (WEB_PLAN.md §3.2). Every hook reads through useSyncExternalStore with snapshots
// the replica caches per collection change version, so a screen re-renders only when the collection it reads
// changed (or, for useRow, only when that one row changed). Outside SyncProvider the hooks return empty values.

import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { Collection } from '@/contracts/collections';
import { SyncContext } from './context';
import type { SyncEngine } from './engine';
import type { FileUrls, FileUrlState } from './files';
import type { SaveResult } from './outbox';
import type { Patch } from './patch';
import type { SyncNotice } from './push';
import type { CachedRow, Replica, RowState } from './replica';

const EMPTY_ROWS: readonly CachedRow[] = Object.freeze([]);
const EMPTY_STATES: ReadonlyMap<string, RowState> = new Map();
const IDLE_FILE: FileUrlState = { status: 'idle', url: null };
const noopSubscribe = () => () => {};

/** The running engine. Throws outside SyncProvider (a programming error: company screens are always inside it). */
export function useSyncEngine(): SyncEngine {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSyncEngine() outside SyncProvider');
  return ctx.engine;
}

/** The engine, or null outside SyncProvider. */
export function useOptionalSyncEngine(): SyncEngine | null {
  return useContext(SyncContext)?.engine ?? null;
}

function useReplica(): Replica | null {
  return useContext(SyncContext)?.engine.replica ?? null;
}

function useCollectionSubscribe(replica: Replica | null, collection: Collection) {
  return useCallback(
    (onChange: () => void) => (replica ? replica.subscribe(collection, onChange) : () => {}),
    [replica, collection],
  );
}

/**
 * Live (non-deleted) rows of a collection as screens should show them: server rows with pending local saves laid
 * over them (optimistic), pending deletes hidden, new unsaved items included. The array keeps its identity until
 * the collection changes. Payloads are raw JSON: decode with the contracts decoders (see useDecodedRows).
 */
export function useRows(collection: Collection): readonly CachedRow[] {
  const replica = useReplica();
  const subscribe = useCollectionSubscribe(replica, collection);
  const read = useCallback(() => (replica ? replica.liveRows(collection) : EMPTY_ROWS), [replica, collection]);
  return useSyncExternalStore(subscribe, read, read);
}

/** One live row (by id, any case); re-renders only when that row changes. */
export function useRow(collection: Collection, id: string | null | undefined): CachedRow | undefined {
  const replica = useReplica();
  const subscribe = useCollectionSubscribe(replica, collection);
  const read = useCallback(() => (replica && id ? replica.liveRow(collection, id) : undefined), [replica, collection, id]);
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * Sync state per row with a local edit, keyed by lower-case id ("Saving…", red "Not saved" + Discard). Rows without
 * an entry are absent (use `rowState(states, id)`).
 */
export function useRowStates(collection: Collection): ReadonlyMap<string, RowState> {
  const replica = useReplica();
  const subscribe = useCollectionSubscribe(replica, collection);
  const read = useCallback(() => (replica ? replica.rowStates(collection) : EMPTY_STATES), [replica, collection]);
  return useSyncExternalStore(subscribe, read, read);
}

/** Looks up one row's state in a useRowStates map. */
export function rowState(states: ReadonlyMap<string, RowState>, id: string | null | undefined): RowState | undefined {
  return id ? states.get(id.toLowerCase()) : undefined;
}

// Decoded values per decoder, per row object. Rows are immutable values (a change stores a new object), so a row
// object decodes once however many screens read it, and only changed rows decode again.
const decodeCache = new WeakMap<object, WeakMap<CachedRow, unknown>>();

/** Decodes a row through `decode`, memoised per row object (the decoder should be a module-level function). */
export function decodeRow<T>(row: CachedRow, decode: (row: CachedRow) => T | null): T | null {
  let perDecoder = decodeCache.get(decode) as WeakMap<CachedRow, T | null> | undefined;
  if (!perDecoder) {
    perDecoder = new WeakMap();
    decodeCache.set(decode, perDecoder);
  }
  if (perDecoder.has(row)) return perDecoder.get(row)!;
  let value: T | null;
  try {
    value = decode(row);
  } catch {
    value = null; // undecodable (a newer app's item): left out, never edited from the web
  }
  perDecoder.set(row, value);
  return value;
}

/**
 * Typed live rows: `decode` turns a row into the screen's type (null leaves it out). Stable array identity until the
 * collection changes; pass a module-level function so the memo holds.
 */
export function useDecodedRows<T>(collection: Collection, decode: (row: CachedRow) => T | null): readonly T[] {
  const rows = useRows(collection);
  return useMemo(() => {
    const out: T[] = [];
    for (const row of rows) {
      const value = decodeRow(row, decode);
      if (value !== null) out.push(value);
    }
    return out;
  }, [rows, decode]);
}

/** One typed live row (null when missing or undecodable). */
export function useDecodedRow<T>(collection: Collection, id: string | null | undefined, decode: (row: CachedRow) => T | null): T | null {
  const row = useRow(collection, id);
  return useMemo(() => (row ? decodeRow(row, decode) : null), [row, decode]);
}

/** `engine.save` (throws outside SyncProvider). */
export function useSave(): (patches: readonly Patch[]) => SaveResult {
  const engine = useSyncEngine();
  return useCallback((patches: readonly Patch[]) => engine.save(patches), [engine]);
}

/** Calls `listener` for every sync notice (toasts). The latest listener is used; no re-subscription per render. */
export function useSyncNotices(listener: (notice: SyncNotice) => void): void {
  const engine = useOptionalSyncEngine();
  const latest = useLatestRef(listener);
  useEffect(() => {
    if (!engine) return;
    return engine.onNotice((notice) => latest.current(notice));
  }, [engine, latest]);
}

function useFiles(): FileUrls | null {
  return useContext(SyncContext)?.files ?? null;
}

/**
 * URL to show a stored photo or document (`file_path` of an attachments/documents row). Asked for in a batch with
 * every other path rendered in the same tick, refreshed before it expires, retried after a while when unavailable.
 */
export function useFileUrl(path: string | null | undefined): FileUrlState {
  const files = useFiles();
  const subscribe = files ? files.subscribe : noopSubscribe;
  const read = useCallback(() => (files ? files.get(path) : IDLE_FILE), [files, path]);
  const state = useSyncExternalStore(subscribe, read, read);
  useEffect(() => {
    if (!files || !path) return;
    if (state.status === 'idle' || (state.status === 'ready' && state.stale)) files.request([path]);
  }, [files, path, state]);
  return state;
}

/** The FileUrls cache (uploads: `files.upload(path, blob)` before pushing the item). Null outside SyncProvider. */
export function useFileUrls(): FileUrls | null {
  return useFiles();
}

/** The latest value in a ref, for a listener registered once. */
function useLatestRef<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}
